import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import { mdiCheck, mdiClose, mdiCheckAll } from '@mdi/js'
import { apiClient } from '../../api/client'
import { notify } from '../../lib/notifications'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'

interface CreatedAssetRow {
  asset_uid: string
  asset_name?: string | null
  asset_type_code?: string | null
  created_at: string
}

interface CreatedAssetsResponse {
  document_uid: string
  total_pending: number
  assets: CreatedAssetRow[]
}

interface Props {
  org: string
  documentUid: string
}

export function DocumentCreatedAssets({ org, documentUid }: Props) {
  const { t } = useTranslation('documents')
  const queryClient = useQueryClient()
  const queryKey = useMemo(
    () => ['document-created-assets', org, documentUid],
    [org, documentUid],
  )

  const { data, isLoading } = useQuery<CreatedAssetsResponse>({
    queryKey,
    queryFn: async () => {
      const { data: resp, error } = await apiClient.GET(
        '/api/v1/organizations/{orgCode}/documents/{documentUid}/created-assets',
        { params: { path: { orgCode: org, documentUid } } } as any,
      )
      if (error) throw new Error(typeof error === 'string' ? error : JSON.stringify(error))
      return resp as CreatedAssetsResponse
    },
    staleTime: 1000 * 60 * 5,
  })

  const [batchProcessing, setBatchProcessing] = useState(false)
  const [processingRow, setProcessingRow] = useState<Set<string>>(new Set())
  const [confirm, setConfirm] = useState<CreatedAssetRow | null>(null)

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey })
    queryClient.invalidateQueries({ queryKey: ['get', '/api/v1/organizations/{orgCode}/assets'] })
  }, [queryClient, queryKey])

  // A failed approve/reject is a silent trap otherwise: the request throws at the
  // network layer, the spinner just stops, and the asset stays pending with no
  // sign anything went wrong. Surface it on the app's notification bus (the same
  // one the API client uses for 403s), never a toast.
  const notifyActionFailed = useCallback(() => {
    notify({
      key: 'document-asset-approval-error',
      variant: 'destructive',
      titleKey: 'documents:pending_asset_creations.action_error_title',
      descriptionKey: 'documents:pending_asset_creations.action_error_body',
    })
  }, [])

  const approveSingle = useCallback(
    async (assetUid: string) => {
      setProcessingRow(prev => new Set(prev).add(assetUid))
      try {
        await apiClient.POST(
          '/api/v1/organizations/{orgCode}/assets/approve',
          {
            params: { path: { orgCode: org } },
            body: { uids: [assetUid], action: 'approve' },
          } as any,
        )
        refresh()
      } catch {
        notifyActionFailed()
      } finally {
        setProcessingRow(prev => {
          const s = new Set(prev)
          s.delete(assetUid)
          return s
        })
      }
    },
    [org, refresh, notifyActionFailed],
  )

  const rejectSingle = useCallback(
    async (assetUid: string) => {
      setProcessingRow(prev => new Set(prev).add(assetUid))
      try {
        await apiClient.POST(
          '/api/v1/organizations/{orgCode}/assets/approve',
          {
            params: { path: { orgCode: org } },
            body: { uids: [assetUid], action: 'reject' },
          } as any,
        )
        refresh()
      } catch {
        notifyActionFailed()
      } finally {
        setProcessingRow(prev => {
          const s = new Set(prev)
          s.delete(assetUid)
          return s
        })
      }
    },
    [org, refresh, notifyActionFailed],
  )

  const validateAll = useCallback(async () => {
    if (!data || data.assets.length === 0) return
    setBatchProcessing(true)
    try {
      await apiClient.POST(
        '/api/v1/organizations/{orgCode}/assets/approve',
        {
          params: { path: { orgCode: org } },
          body: { uids: data.assets.map(a => a.asset_uid), action: 'approve' },
        } as any,
      )
      refresh()
    } catch {
      notifyActionFailed()
    } finally {
      setBatchProcessing(false)
    }
  }, [data, org, refresh, notifyActionFailed])

  if (isLoading || !data) return null
  if (data.total_pending === 0) return null

  return (
    <>
      <div
        className="space-y-3 mb-6 border border-border rounded-lg p-4 bg-amber-50/30 dark:bg-amber-900/10"
        data-testid="document-created-assets"
      >
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              {t('pending_asset_creations.title')}
            </h3>
            <p className="text-xs text-muted-foreground">
              {t('pending_asset_creations.summary', { count: data.total_pending })}
            </p>
          </div>
          {data.total_pending > 1 && (
            <Button
              variant="default"
              size="sm"
              onClick={validateAll}
              disabled={batchProcessing}
              data-testid="document-validate-all-assets-btn"
            >
              <AppIcon path={mdiCheckAll} size="sm" className="mr-1" />
              {t('pending_asset_creations.validate_all', { count: data.total_pending })}
            </Button>
          )}
        </div>

        <div className="space-y-1">
          {data.assets.map(a => (
            <div
              key={a.asset_uid}
              className="flex items-center gap-2 px-3 py-1.5 border-b last:border-b-0 border-border/50"
              data-testid={`document-pending-asset-row-${a.asset_uid}`}
            >
              {a.asset_type_code && (
                <span className="text-xs text-muted-foreground uppercase tracking-wide flex-shrink-0">
                  {a.asset_type_code}
                </span>
              )}
              <Link
                to="/$org/assets/$assetUid"
                params={{ org, assetUid: a.asset_uid }}
                className="text-sm font-medium text-foreground hover:underline flex-shrink min-w-0 truncate"
              >
                {a.asset_name || a.asset_uid}
              </Link>
              <div className="flex-1" />
              <div className="flex gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                  onClick={() => approveSingle(a.asset_uid)}
                  disabled={processingRow.has(a.asset_uid)}
                  data-testid={`document-approve-asset-${a.asset_uid}`}
                >
                  <AppIcon path={mdiCheck} size="sm" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => setConfirm(a)}
                  disabled={processingRow.has(a.asset_uid)}
                  data-testid={`document-reject-asset-${a.asset_uid}`}
                >
                  <AppIcon path={mdiClose} size="sm" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <AlertDialog open={confirm !== null} onOpenChange={open => { if (!open) setConfirm(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('pending_asset_creations.reject_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('pending_asset_creations.reject_confirm_body', {
                name: confirm?.asset_name || confirm?.asset_uid || '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('pending_asset_creations.reject_cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirm) rejectSingle(confirm.asset_uid)
                setConfirm(null)
              }}
              data-testid="document-reject-asset-confirm"
            >
              {t('pending_asset_creations.reject_confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
